// Couche d'identité de l'application, indépendante du mode d'authentification.
//
// - Mode « oidc » (production, pile de démonstration) : OpenID Connect, flux « code » avec PKCE (oidc.ts), contre
//   le fournisseur d'identité de l'entreprise. Le jeton d'accès de la personne est envoyé à l'API, qui le vérifie ;
//   aucun relais n'ajoute de jeton. Les rôles affichés sont ceux que l'API déduit du jeton (GET /v0/moi).
// - Mode « developpement » (poste de développement seulement, API en EKERA_API_MODE=developpement) : l'API n'accepte
//   qu'un jeton porteur fixe, ajouté par le relais de Vite (EKERA_API_JETON, lue côté serveur) ; le navigateur n'en
//   connaît aucun. L'auteur des écritures est déclaratif (VITE_EKERA_UTILISATEUR).
//
// Configuration : /config.json, servi à l'exécution par le serveur de l'application (une même image pour toutes les
// installations), puis, à défaut, les variables VITE_* empaquetées à la construction.

export interface Utilisateur {
  nom: string;
  /** Identifiant stable (« sub » OIDC) ; absent en mode développement. */
  sujet?: string;
}

export interface FournisseurIdentite {
  readonly mode: 'developpement' | 'oidc';
  /** L'utilisateur connecté, ou null s'il faut se connecter. */
  utilisateur(): Utilisateur | null;
  /** En-têtes d'authentification à joindre à chaque appel de l'API (jeton renouvelé si besoin). */
  enTetes(): Promise<Record<string, string>>;
  connecter(): Promise<void>;
  deconnecter(): Promise<void>;
  /** Traite le retour du fournisseur d'identité (URL de redirection) ; vrai si l'URL en était un. */
  traiterRetour(url: URL): Promise<boolean>;
  /** Écran (fragment « #/… ») à rouvrir après le retour du fournisseur. */
  readonly ecranApresRetour?: string;
  /** Prévient quand l'utilisateur change (connexion, session expirée) ; rend la fonction de désabonnement. */
  surChangement?(rappel: () => void): () => void;
  /**
   * Prévient après chaque renouvellement réussi du jeton d'accès (W2) : les rôles et l'organisation que l'API déduit du
   * nouveau jeton ont pu changer, l'écran relit alors GET /v0/moi. L'API reste l'autorité sur les droits.
   */
  surRenouvellement?(rappel: () => void): () => void;
}

/** Configuration lue à l'exécution (/config.json). Rien de secret : des URL et des identifiants publics. */
export interface ConfigurationApplication {
  authentification?: 'oidc' | 'developpement';
  oidc?: { emetteur?: string; client_id?: string; portee?: string; redirection?: string };
  carte?: string;
  /**
   * W3 : vrai seulement pour une démonstration déclarée par la configuration de confiance (entreprise et personnes
   * fictives) ; la mention « Entreprise et données fictives » est alors affichée. Absent ou faux : jamais affichée.
   */
  demonstration?: boolean;
}

export class IdentiteDeveloppement implements FournisseurIdentite {
  readonly mode = 'developpement' as const;
  private readonly nom: string;

  constructor(nom?: string) {
    this.nom = nom?.trim() || 'Thomas Garnier, DSI';
  }

  utilisateur(): Utilisateur {
    return { nom: this.nom };
  }

  async enTetes(): Promise<Record<string, string>> {
    // Le jeton porteur de l'API (mode développement) est ajouté par le relais de développement (vite.config.ts).
    return {};
  }

  async connecter(): Promise<void> {}
  async deconnecter(): Promise<void> {}
  async traiterRetour(): Promise<boolean> {
    return false;
  }
}

/** Lit /config.json (absent en développement : configuration vide). */
export async function lireConfiguration(recuperer: typeof fetch = (...a) => fetch(...a)): Promise<ConfigurationApplication> {
  try {
    const r = await recuperer('/config.json', { cache: 'no-store' });
    if (!r.ok || !(r.headers.get('Content-Type') ?? '').includes('json')) return {};
    return (await r.json()) as ConfigurationApplication;
  } catch {
    return {};
  }
}

/** Choisit le fournisseur d'identité : /config.json d'abord, puis les variables VITE_* de la construction. */
export async function creerIdentite(
  env: Record<string, string | undefined>,
  config: ConfigurationApplication = {},
): Promise<FournisseurIdentite> {
  const mode = config.authentification ?? (env.VITE_EKERA_AUTH === 'oidc' ? 'oidc' : 'developpement');
  if (mode === 'oidc') {
    const { IdentiteOidc } = await import('./oidc');
    const o = config.oidc ?? {};
    return new IdentiteOidc({
      emetteur: o.emetteur ?? exiger(env, 'VITE_OIDC_EMETTEUR'),
      clientId: o.client_id ?? exiger(env, 'VITE_OIDC_CLIENT_ID'),
      redirection: o.redirection ?? env.VITE_OIDC_REDIRECTION ?? `${window.location.origin}/`,
      portee: o.portee ?? env.VITE_OIDC_PORTEE ?? 'openid profile email',
    });
  }
  return new IdentiteDeveloppement(env.VITE_EKERA_UTILISATEUR);
}

function exiger(env: Record<string, string | undefined>, cle: string): string {
  const v = env[cle];
  if (!v) throw new Error(`configuration OIDC incomplète : ${cle} manquante (ni /config.json, ni variable de construction)`);
  return v;
}
